import { LogSeverity, QStatus, QueueStatusGroup } from '../synchronization.model';

/**
 * Wire contracts for `/SynchronizationMonitoring/*`, plus the filter state the browser keeps.
 *
 * Timestamps stay as the raw ISO strings the server emitted. Angular's DatePipe renders a string
 * directly, so nothing has to parse them for display — and keeping them verbatim is what lets a
 * cursor round-trip without losing the sub-millisecond precision of datetime2(7).
 */

// ── Scope and templates ──────────────────────────────────────────────────────

/**
 * The dropdown data, and the client's name table: the queue endpoints return ids only, so customer,
 * company and synchronization names are all resolved from here.
 */
export interface MonitoringScopeDto {
    customers: MonitoringScopeCustomerDto[];
}

interface MonitoringScopeCustomerDto {
    customerId: number;
    customerName: string;
    synchronizations: MonitoringScopeSynchronizationDto[];
}

interface MonitoringScopeSynchronizationDto {
    id: string;
    name: string;
    companyId: number;
    companyName: string;
}

export interface MonitoringTemplateDto {
    id: string;
    name: string;
}

// ── The list ─────────────────────────────────────────────────────────────────

export interface QueueRequestListItemDto {
    id: string;
    customerId: number;
    companyId: number;
    synchronizationId: string;
    templateId: string;
    templateName: string | null;
    action: string;
    task: string;
    description: string;
    status: QStatus | null;
    /** Server-assigned and total — never recomputed here, or the badges and the list could disagree. */
    statusGroup: Exclude<QueueStatusGroup, 'all'>;
    /** Raw ISO. The single field the window, the ordering and the cursor all agree on. */
    lastActivity: string;
    /** C# TimeSpan string, so FormatRuntimePipe renders it unchanged. */
    runtime: string | null;
    executerTotal: number;
    /** Chunks that have stopped, successfully or not. `executerFailed` is a subset. */
    executerFinished: number;
    executerFailed: number;
}

/** `[all, running, finished, warning, failed]`. */
export type CountsArray = [number, number, number, number, number];

/**
 * Every window × every group, from `/queues/counts`. Read by indexing, never by refetching:
 *
 *   date badge for window W  = map[W][0]
 *   status badge for group G = map[selectedWindow][G]
 *
 * That is what makes the badges tiered — each control reflects the filters above it and nothing at
 * or below it — and it is why clicking a window or status button issues no request at all.
 */
export interface MonitoringCountsMap {
    sevenDays: CountsArray;
    twentyFourHours: CountsArray;
    oneHour: CountsArray;
    /** Null until a date is picked — the day badge then shows "*" rather than a misleading 0. */
    day: CountsArray | null;
}

export interface QueuesResponseDto {
    items: QueueRequestListItemDto[];
    hasMore: boolean;
    anchorIncluded: boolean;
}

export interface QueueUpdatesResponseDto {
    items: QueueRequestListItemDto[];
}

// ── The detail pane ──────────────────────────────────────────────────────────

/** One QueueTimeTracker row: the moment a kørsel or delkørsel entered a status. Raw ISO. */
export interface QueuePhaseDto {
    status: QStatus | null;
    start: string;
}

export interface QueueExecuterDto {
    id: string;
    /** 1-based chunk number. */
    current: number;
    total: number;
    status: QStatus | null;
    /**
     * Error text for this chunk. Authoritative until the four Synchronize handlers in
     * Crono-Synchronization start writing QueueLog rows — today they record failure only here.
     */
    message: string;
    dbTableFullName: string;
    /** -1 means the source has not counted the rows yet. */
    itemsInData: number;
    hasPayload: boolean;
    /**
     * First tracker row of the chunk. Null for kørsler older than 2026-09, whose chunk rows were
     * written under random ids and cannot be told apart.
     */
    startedAt: string | null;
    /** The chunk's terminal tracker row; null while it runs. */
    completedAt: string | null;
    /** Executing → PrepareExecute → ReadDataExecute → SyncDataExecute → Done, oldest first. Admin-only in the UI. */
    phases: QueuePhaseDto[];
}

export interface QueueLogEntryDto {
    id: string;
    timestamp: string;
    severity: LogSeverity;
    message: string;
    origin: string;
    /** The chunk this line belongs to, or null for kørsel-level lines. */
    executerId: string | null;
}

export interface QueueRequestDetailDto {
    id: string;
    customerId: number;
    companyId: number;
    synchronizationId: string;
    templateId: string;
    templateName: string | null;
    action: string;
    task: string;
    description: string;
    status: QStatus | null;
    statusGroup: Exclude<QueueStatusGroup, 'all'>;
    lastActivity: string;
    created: string | null;
    runtime: string | null;
    /** When the status rollup finished the kørsel. Null while running and for kørsler older than the column. */
    completedAt: string | null;
    /** Kørsel-level phases (Waiting, Validating, Queued, pre-synk …), oldest first. Admin-only in the UI. */
    timeline: QueuePhaseDto[];
    executers: QueueExecuterDto[];
    logs: QueueLogEntryDto[];
}

// ── Filter state ─────────────────────────────────────────────────────────────

export type QueueTimeWindow = '7d' | '24h' | '1h' | 'day';

export const ROLLING_WINDOWS: readonly { key: Exclude<QueueTimeWindow, 'day'>; label: string }[] = [
    { key: '7d', label: '7 dage' },
    { key: '24h', label: '24 timer' },
    { key: '1h', label: '1 time' }
];

/** The day picker covers the same span as the widest rolling window. */
export const DAY_PICKER_DAYS = 7;

/**
 * Every filter, all of them server-side. One object rather than five signals, so picking a date can
 * set `window: 'day'` and `day` in a single write — two writes would publish an intermediate
 * `{ window: 'day', day: null }`, which is a valid query meaning "everything, no window".
 *
 * `search` belongs here rather than client-side because the badges have to honour it — a badge that
 * ignores the search box is worse than no badge.
 */
export interface QueueQueryFilters {
    templateId: string | null;
    /** Debounced before it reaches either resource key. */
    search: string;
    window: QueueTimeWindow;
    /** 'yyyy-MM-dd', local. A string, not a Date: two Dates for the same day break value equality. */
    day: string | null;
    statusGroup: QueueStatusGroup;
}

export const DEFAULT_QUERY_FILTERS: QueueQueryFilters = {
    templateId: null,
    search: '',
    window: '24h',
    day: null,
    statusGroup: 'all'
};

// ── View models ──────────────────────────────────────────────────────────────

/** A list item with the names resolved and progress unpacked, ready to render. */
export interface QueueRequestRow {
    item: QueueRequestListItemDto;
    customerName: string;
    companyName: string;
    synchronizationName: string;
    progress: { done: number; total: number; failed: number; percent: number };
}

export type ExecutorGroupKey = 'failed' | 'running' | 'waiting' | 'done';

export interface ExecutorGroup {
    key: ExecutorGroupKey;
    label: string;
    variant: string;
    executers: QueueExecuterDto[];
}
